/**
 * Transaction process graph for apartment rentals:
 *   - rent-apartment
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 */

export const transitions = {
  // Initial inquiry
  INQUIRE: 'transition/inquire',
  
  // Request payment transitions (includes pending booking creation)
  REQUEST_PAYMENT: 'transition/request-payment',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',
  
  // Provider/Operator actions
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',
  EXPIRE: 'transition/expire',
  OPERATOR_EXPIRE: 'transition/operator-expire',
  
  // Guests confirmation
  CONFIRM_GUESTS: 'transition/confirm-guests',
  MARK_NOT_COMPLETED: 'transition/mark-not-completed',
  OPERATOR_MARK_NOT_COMPLETED: 'transition/operator-mark-not-completed',
  
  // Cancellation
  CANCEL: 'transition/cancel',
  COMPLETING_CANCELLATION: 'transition/completing-cancellation',
  // NEW: Add new transitions for customers to cancel reservations before and after the
  // host accepts the booking.
  CANCEL_BEFORE_ACCEPT: 'transition/cancel-before-accept',
  CANCEL_AFTER_ACCEPT: 'transition/cancel-after-accept',
  
  // Completion
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',
  COMPLETING: 'transition/completing',
  
  // Reviews
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  SUBSCRIBE: 'subscribe',
  WAITING_GUESTS: 'waiting-guests',
  SUBSCRIBED: 'subscribed',
  DECLINED: 'declined',
  EXPIRED: 'expired',
  CANCELLED_BY_CUSTOMER_BA: 'cancelled-by-customer-ba', // Before Accept
  CANCELLED_BY_CUSTOMER_AA: 'cancelled-by-customer-aa', // After Accept
  CANCELLED: 'cancelled',
  NOT_COMPLETED: 'not-completed',
  COMPLETE_CYCLE: 'complete-cycle',
  CYCLE_COMPLETED: 'cycle-completed',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Transaction process graph for apartment rentals
 */
export const graph = {
  id: 'rent-apartment/release-1',
  initial: states.INITIAL,
  
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.SUBSCRIBE,
      },
    },
    
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.SUBSCRIBE,
      },
    },

    [states.SUBSCRIBE]: {
      on: {
        [transitions.ACCEPT]: states.WAITING_GUESTS,
        [transitions.OPERATOR_ACCEPT]: states.WAITING_GUESTS,
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.OPERATOR_EXPIRE]: states.EXPIRED,
        [transitions.CANCEL_BEFORE_ACCEPT]: states.CANCELLED_BY_CUSTOMER_BA,
      },
    },

    [states.WAITING_GUESTS]: {
      on: {
        [transitions.CONFIRM_GUESTS]: states.SUBSCRIBED,
        [transitions.MARK_NOT_COMPLETED]: states.NOT_COMPLETED,
        [transitions.OPERATOR_MARK_NOT_COMPLETED]: states.NOT_COMPLETED,
        [transitions.CANCEL_AFTER_ACCEPT]: states.CANCELLED_BY_CUSTOMER_AA,
      },
    },

    [states.SUBSCRIBED]: {
      on: {
        [transitions.CANCEL]: states.CANCELLED,
        [transitions.COMPLETE]: states.COMPLETE_CYCLE,
        [transitions.OPERATOR_COMPLETE]: states.CYCLE_COMPLETED,
      },
    },

    [states.COMPLETE_CYCLE]: {
      on: {
        [transitions.COMPLETING]: states.CYCLE_COMPLETED,
      },
    },

    [states.CYCLE_COMPLETED]: {
      on: {
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    
    [states.CANCELLED]: {
        on: {
            [transitions.COMPLETING_CANCELLATION]: states.CYCLE_COMPLETED,
        },
    },


    [states.REVIEWED]: {},

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
  },
};

// Check if a transition is relevant for activity history
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.CANCEL,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.COMPLETING,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
  ].includes(transition);
};

export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.COMPLETING,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ].includes(transition);
  return txCompletedTransitions.includes(transition);
};

export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [
  states.SUBSCRIBE,
  states.SUBSCRIBED,
];